import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Routes, Route, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import {
  HelpCircle,
  MessageSquare,
  Plus,
  Clock,
  CheckCircle,
  AlertCircle,
  Search,
  Calendar,
  ArrowLeft,
  Send,
  Paperclip,
  X,
  ExternalLink,
  Image as ImageIcon,
  TrendingUp,
  ArrowUpRight,
  Flag,
  XCircle,
  MessageCircle,
  Edit3,
  Zap,
  AlertTriangle,
  Circle
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import { Button } from '../ui/button'
import { Card, CardContent } from '../ui/card'
import { cn } from '../../utils/cn'

const statusConfig = {
  open:        { color: 'bg-accent-100 text-accent-800 dark:bg-accent-900/30 dark:text-accent-200',     icon: AlertCircle,  label: 'Open' },
  in_progress: { color: 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-200', icon: Clock,        label: 'In Progress' },
  resolved:    { color: 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-200', icon: CheckCircle,  label: 'Resolved' },
  closed:      { color: 'bg-neutral-100 text-neutral-800 dark:bg-neutral-800/30 dark:text-neutral-200', icon: XCircle,      label: 'Closed' },
}

const priorityDot = {
  urgent: { bg: 'bg-error-50 dark:bg-error-900/20',    icon: <AlertCircle  className="w-4 h-4 text-error-600 dark:text-error-400" /> },
  high:   { bg: 'bg-warning-50 dark:bg-warning-900/20', icon: <TrendingUp   className="w-4 h-4 text-warning-600 dark:text-warning-400" /> },
  medium: { bg: 'bg-accent-50 dark:bg-accent-900/20',  icon: <ArrowUpRight  className="w-4 h-4 text-accent-600 dark:text-accent-400" /> },
  low:    { bg: 'bg-neutral-100 dark:bg-neutral-800',   icon: <Flag         className="w-4 h-4 text-neutral-400" /> },
}

// Matches admin's getTimeAgo
const getTimeAgo = (timestamp) => {
  const diff = Date.now() - new Date(timestamp)
  const m = Math.floor(diff / 60000)
  const h = Math.floor(diff / 3600000)
  const d = Math.floor(diff / 86400000)
  if (m < 1)  return 'Just now'
  if (m < 60) return `${m}m ago`
  if (h < 24) return `${h}h ago`
  if (d < 7)  return `${d}d ago`
  return new Date(timestamp).toLocaleDateString()
}

const SupportTicketsList = () => {
  const [searchQuery, setSearchQuery]       = useState('')
  const [statusFilter, setStatusFilter]     = useState('all')
  const [selectedTicketId, setSelectedTicketId] = useState(null)
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const openId = searchParams.get('open')
    if (openId) setSelectedTicketId(openId)
  }, [searchParams])

  const { data: tickets = [], isLoading, error } = useQuery(
    ['support-tickets', statusFilter],
    () => apiService.get('/support/tickets', {
      params: { status: statusFilter !== 'all' ? statusFilter : undefined }
    }).then(res => res.data),
    { staleTime: 2 * 60 * 1000, retry: 1 }
  )

  const filteredTickets = tickets.filter(t =>
    t.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  if (error) {
    return (
      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardContent className="text-center py-12">
          <AlertCircle className="h-16 w-16 text-error-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-2">Error Loading Support Tickets</h3>
          <p className="text-neutral-500 dark:text-neutral-400">{error.response?.data?.error || 'Failed to load support tickets'}</p>
          <Button onClick={() => queryClient.invalidateQueries('support-tickets')} className="mt-4">Try Again</Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">Support Center</h1>
          <p className="text-neutral-600 dark:text-neutral-400">Get help and track your support requests</p>
        </div>
        <Button asChild>
          <Link to="/client/support/new">
            <Plus className="h-4 w-4 mr-2" />
            New Ticket
          </Link>
        </Button>
      </div>

      {/* Search and Filters */}
      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Search tickets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9 w-full pl-10 pr-3 border border-neutral-200 dark:border-[#2f2b41] rounded-md bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-500 dark:placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent text-sm"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 sm:w-44 px-3 border border-neutral-200 rounded-md bg-white text-neutral-900 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 dark:bg-[#221f2f] dark:border-[#2f2b41] dark:text-neutral-100 appearance-none cursor-pointer"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Tickets list */}
      {isLoading ? (
        <Card className="border-neutral-200 dark:border-[#2f2b41]">
          <CardContent className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" text="Loading support tickets..." />
          </CardContent>
        </Card>
      ) : filteredTickets.length > 0 ? (
        <div className="space-y-3">
          {filteredTickets.map((ticket) => {
            const dot = priorityDot[ticket.priority] || priorityDot.medium
            const sc  = statusConfig[ticket.status]  || statusConfig.open
            const StatusIcon = sc.icon
            return (
              <motion.div
                key={ticket.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="p-5 border border-neutral-200 dark:border-[#2f2b41] rounded-xl bg-white dark:bg-[#252232] hover:border-accent-300 dark:hover:border-accent-600 hover:shadow-md transition-all cursor-pointer"
                onClick={() => setSelectedTicketId(ticket.id)}
              >
                <div className="flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={cn('w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5', dot.bg)}>
                        {dot.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate mb-2">
                          {ticket.subject}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
                          <span className="flex items-center gap-1 capitalize"><Flag className="w-3 h-3 flex-shrink-0" />{ticket.priority} priority</span>
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3 flex-shrink-0" />{getTimeAgo(ticket.created_at)}</span>
                          {ticket.updated_at !== ticket.created_at && (
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3 flex-shrink-0" />Updated {getTimeAgo(ticket.updated_at)}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Desktop: badge on the right */}
                    <span className={cn('hidden sm:inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0', sc.color)}>
                      <StatusIcon className="w-3 h-3" />
                      {sc.label}
                    </span>
                  </div>
                  {/* Mobile: badge as bottom row */}
                  <div className="flex sm:hidden">
                    <span className={cn('inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium', sc.color)}>
                      <StatusIcon className="w-3 h-3" />
                      {sc.label}
                    </span>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      ) : (
        <Card className="border-neutral-200 dark:border-[#2f2b41]">
          <CardContent className="text-center py-12">
            <MessageSquare className="h-16 w-16 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-2">
              {searchQuery || statusFilter !== 'all' ? 'No tickets found' : 'No support tickets yet'}
            </h3>
            <p className="text-neutral-500 dark:text-neutral-400 mb-6">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Create your first support ticket to get help from our team.'}
            </p>
            <Button asChild>
              <Link to="/client/support/new">
                <Plus className="h-4 w-4 mr-2" />Create Support Ticket
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <AnimatePresence>
        {selectedTicketId && (
          <TicketDetailModal
            ticketId={selectedTicketId}
            onClose={() => setSelectedTicketId(null)}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

const TicketDetailModal = ({ ticketId, onClose }) => {
  const queryClient = useQueryClient()
  const [replyText, setReplyText] = useState('')

  const { data: ticket, isLoading } = useQuery(
    ['support-ticket', ticketId],
    () => apiService.get(`/support/${ticketId}`).then(res => res.data),
    { enabled: !!ticketId }
  )

  const { data: replies = [], isLoading: repliesLoading } = useQuery(
    ['support-replies', ticketId],
    () => apiService.get(`/support/${ticketId}/replies`).then(res => res.data),
    { enabled: !!ticketId, staleTime: 30 * 1000 }
  )

  const replyMutation = useMutation(
    (message) => apiService.post(`/support/${ticketId}/replies`, { message }),
    {
      onSuccess: () => {
        toast.success('Reply sent!')
        queryClient.invalidateQueries(['support-replies', ticketId])
        queryClient.invalidateQueries(['support-ticket', ticketId])
        queryClient.invalidateQueries('support-tickets')
        setReplyText('')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to send reply')
      }
    }
  )

  const sc  = statusConfig[ticket?.status]  || statusConfig.open
  const dot = priorityDot[ticket?.priority] || priorityDot.medium
  const StatusIcon = sc.icon

  return (
    createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <motion.div
          className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        />
        <motion.div
          className="relative w-full max-w-3xl bg-white dark:bg-[#1a1625] rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner size="lg" text="Loading ticket..." />
            </div>
          ) : ticket ? (
            <>
              {/* Header */}
              <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-neutral-100 dark:border-[#2f2b41]">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 leading-snug truncate">
                      {ticket.subject}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(ticket.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={cn('hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium', sc.color)}>
                      <StatusIcon className="w-3 h-3" />
                      {sc.label}
                    </span>
                    <div className={cn('hidden sm:flex w-7 h-7 rounded-full items-center justify-center', dot.bg)}>
                      {dot.icon}
                    </div>
                    <Button onClick={onClose} variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto">
                <div className="flex flex-col lg:flex-row gap-0 lg:gap-6 p-6">

                  {/* Left: conversation */}
                  <div className="flex-1 min-w-0 space-y-4">
                    {/* Original message */}
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2 flex items-center gap-1.5">
                        <MessageCircle className="w-3.5 h-3.5" /> Message
                      </p>
                      <div className="bg-neutral-50 dark:bg-[#1f1c2c] rounded-xl p-4 border border-neutral-200 dark:border-[#2f2b41]">
                        <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap leading-relaxed">
                          {ticket.message}
                        </p>
                      </div>
                    </div>

                    {/* Screenshot */}
                    {ticket.screenshot_url && (
                      <a
                        href={ticket.screenshot_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-accent-50 dark:bg-accent-900/20 rounded-lg text-sm text-accent-600 dark:text-accent-400 hover:bg-accent-100 dark:hover:bg-accent-900/30 transition-colors"
                      >
                        <ImageIcon className="w-4 h-4" />
                        <span className="font-medium">View attachment</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}

                    {/* Conversation thread */}
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2 flex items-center gap-1.5">
                        <MessageCircle className="w-3.5 h-3.5" /> Conversation
                      </p>
                      <div className="space-y-2">
                        {/* Legacy response_message */}
                        {ticket.response_message && replies.length === 0 && (
                          <div className="bg-accent-50 dark:bg-accent-900/20 border border-accent-200 dark:border-accent-700 rounded-xl p-4">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-xs font-medium text-accent-600 dark:text-accent-400">Support Team</span>
                              {ticket.responded_at && (
                                <span className="text-xs text-neutral-400">{new Date(ticket.responded_at).toLocaleString()}</span>
                              )}
                            </div>
                            <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap leading-relaxed">
                              {ticket.response_message}
                            </p>
                          </div>
                        )}

                        {repliesLoading ? (
                          <p className="text-xs text-neutral-400 dark:text-neutral-500 py-2">Loading conversation…</p>
                        ) : replies.length === 0 && !ticket.response_message ? (
                          <p className="text-xs text-neutral-400 dark:text-neutral-500 italic py-2">
                            No replies yet. Our team will get back to you shortly.
                          </p>
                        ) : (
                          replies.map(reply => (
                            <div key={reply.id} className={cn(
                              "rounded-xl p-4 border",
                              reply.role === 'admin'
                                ? "bg-success-50 dark:bg-success-900/20 border-success-200 dark:border-success-800"
                                : "bg-neutral-50 dark:bg-[#1f1c2c] border-neutral-200 dark:border-[#2f2b41]"
                            )}>
                              <div className="flex items-center gap-2 mb-1.5">
                                <span className={cn(
                                  "text-xs font-medium",
                                  reply.role === 'admin' ? "text-success-600 dark:text-success-400" : "text-neutral-600 dark:text-neutral-400"
                                )}>
                                  {reply.role === 'admin' ? 'Support Team' : 'You'}
                                </span>
                                <span className="text-xs text-neutral-400">{new Date(reply.created_at).toLocaleString()}</span>
                              </div>
                              <p className="text-sm text-neutral-800 dark:text-neutral-200 whitespace-pre-wrap leading-relaxed">
                                {reply.message}
                              </p>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Reply form */}
                    {ticket.status !== 'closed' && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2 flex items-center gap-1.5">
                          <Edit3 className="w-3.5 h-3.5" /> Reply
                        </p>
                        <textarea
                          rows={4}
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Write your reply here…"
                          className="w-full px-3 py-2.5 border border-neutral-200 dark:border-[#2f2b41] rounded-xl bg-white dark:bg-[#221f2f] text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent resize-none"
                        />
                        <div className="mt-3">
                          <Button
                            onClick={() => replyText.trim() && replyMutation.mutate(replyText.trim())}
                            disabled={!replyText.trim() || replyMutation.isLoading}
                            size="sm"
                            className="flex items-center gap-1.5"
                          >
                            <Send className="w-3.5 h-3.5" />
                            {replyMutation.isLoading ? 'Sending…' : 'Send Reply'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right: details */}
                  <div className="w-full lg:w-44 flex-shrink-0 mt-4 lg:mt-0">
                    <div className="lg:sticky lg:top-0 space-y-1.5">
                      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-2">Details</p>
                      {[
                        ['Status',   <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium', sc.color)}><StatusIcon className="w-3 h-3" />{sc.label}</span>],
                        ['Priority', <span className="capitalize">{ticket.priority}</span>],
                        ['Opened',   new Date(ticket.created_at).toLocaleDateString()],
                        ['Updated',  ticket.updated_at !== ticket.created_at ? new Date(ticket.updated_at).toLocaleDateString() : null],
                      ].filter(([, v]) => v).map(([label, value]) => (
                        <div key={label} className="flex justify-between gap-2 text-xs">
                          <span className="text-neutral-500 dark:text-neutral-400 shrink-0">{label}</span>
                          <span className="text-neutral-900 dark:text-neutral-100 text-right">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </div>
            </>
          ) : null}
        </motion.div>
    </div>,
    document.body
  ))
}

const NewSupportTicket = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    priority: 'medium'
  })
  const [attachments, setAttachments] = useState([])

  const createTicketMutation = useMutation(
    (data) => {
      const formDataObj = new FormData()
      formDataObj.append('subject', data.subject)
      formDataObj.append('message', data.message)
      formDataObj.append('priority', data.priority)
      if (data.screenshot) formDataObj.append('screenshot', data.screenshot)
      return apiService.post('/support', formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
    },
    {
      onSuccess: () => {
        toast.success('Support ticket created successfully!')
        queryClient.invalidateQueries('support-tickets')
        navigate('/client/support')
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create support ticket')
      }
    }
  )

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.subject.trim() || !formData.message.trim()) {
      toast.error('Please fill in all required fields')
      return
    }
    createTicketMutation.mutate({ ...formData, screenshot: attachments[0] })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">Create Support Ticket</h1>
          <p className="text-neutral-600 dark:text-neutral-400">Tell us about the issue you're experiencing</p>
        </div>
        <Button asChild variant="ghost" className="dark:bg-[#221f2f] dark:text-neutral-300 dark:hover:bg-[#2f2b41] dark:hover:text-neutral-100">
          <Link to="/client/support">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Support
          </Link>
        </Button>
      </div>

      <Card className="border-neutral-200 dark:border-[#2f2b41]">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Subject *</label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Brief description of your issue"
                className="w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Message *</label>
              <textarea
                rows={6}
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Please provide detailed information about your issue..."
                className="w-full px-3 py-2 border border-neutral-200 dark:border-[#2f2b41] rounded-lg focus:ring-2 focus:ring-accent-500 focus:border-transparent bg-white dark:bg-[#221f2f] text-neutral-900 dark:text-neutral-100"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Screenshot (Optional)</label>
              <div className="border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-lg p-6 text-center">
                <input
                  type="file"
                  onChange={(e) => setAttachments(Array.from(e.target.files))}
                  accept="image/*"
                  className="hidden"
                  id="screenshot-upload"
                />
                <label htmlFor="screenshot-upload" className="cursor-pointer">
                  <Paperclip className="h-8 w-8 text-neutral-400 mx-auto mb-2" />
                  <p className="text-neutral-500 dark:text-neutral-400">Click to upload a screenshot (optional)</p>
                  <p className="text-xs text-neutral-400 mt-1">PNG, JPG, GIF up to 5MB</p>
                </label>
                {attachments.length > 0 && (
                  <p className="mt-2 text-sm text-success-600">{attachments[0].name} selected</p>
                )}
              </div>
            </div>

            <div className="flex space-x-4">
              <Button type="submit" disabled={createTicketMutation.isLoading}>
                {createTicketMutation.isLoading ? <LoadingSpinner size="sm" /> : (
                  <><Send className="h-4 w-4 mr-2" />Create Ticket</>
                )}
              </Button>
              <Button asChild variant="outline">
                <Link to="/client/support">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

const ClientSupport = () => {
  return (
    <Routes>
      <Route index element={<SupportTicketsList />} />
      <Route path="new" element={<NewSupportTicket />} />
    </Routes>
  )
}

export default ClientSupport
